import { SSMT } from '../../../constants/qnames';

/**
 * Shared business rules for Role 2 (Scope of Filing).
 *
 * Both role2-020000-scope-of-filing.component.ts AND the pure
 * role2ScopeOfFilingValidator import from this file so the "is this control
 * unconditionally required?" projection stays DRY across:
 *
 *   - the component's buildForm() Validators.required wiring
 *   - the pure validator's required-field check
 *
 * Why this is a shared helper rather than inlined:
 *   The schema mixes two ways of declaring "required":
 *     (a) top-level `"required": true` on the control, and
 *     (b) `"validators": [{ "name": "required" }]` entries.
 *   The editor's buildForm() originally only honoured (a), silently dropping
 *   required enforcement for two controls declared via (b) only. The role 6
 *   lift fixed the analogous bug; role 2 applies the same expansion here.
 *   Keeping the detector central prevents the two paths from drifting again.
 *
 * CLBG branching (FS-CLBG / KFI-CLBG submission types):
 *   CLBG-specific fields carry `ui.clbgTable === true` AND participate in
 *   dimensional axes under `ContextPolicy.allowedAxes` that are CLBG-only
 *   (e.g. `ssmt-clbg:ObjectOneMember`). The component's buildForm only
 *   attaches `Validators.required` to axis fields when the axis equals
 *   `ssmt:BusinessOneMember`, so CLBG axis controls are never flagged
 *   required on the form — regardless of submission type. Nothing filters
 *   CLBG fields out of the payload on submit (the `FormSanitizationService`
 *   once cited here was never wired in, and was deleted 2026-10-21). The pure
 *   validator mirrors the form:
 *   it only treats `ssmt:BusinessOneMember`-axis controls as required when
 *   the schema also has `required: true`. No CLBG-specific required gating
 *   is applied, matching mount-based `scopeForm.valid`.
 *
 * Modify rules HERE only — anywhere else risks the parity-check harness
 * throwing in dev because the two paths disagree.
 */

/** Axis name for the first business row; the only axis the editor's
 * buildForm() attaches `Validators.required` to. CLBG axes (ObjectOne*,
 * ObjectTwo*, ObjectThree*) intentionally fall outside this gate. */
export const ROLE2_REQUIRED_AXIS = SSMT.BusinessOneMember;

/**
 * Minimal shape both the component and validator consume. Narrow on purpose
 * so it can alias the runtime `FormControlSchema` without dragging its full
 * UI surface through the validator module.
 */
export interface Role2RequiredDetectable {
  required?: boolean;
  validators?: ReadonlyArray<{ name?: string; conditionalOn?: unknown }>;
  ui?: { readonly?: boolean };
}

/**
 * A DISABLED control fires no validations — the same rule adopted for
 * role0/role1 in `filing-info-common.rules.ts` (`isControlDisabled`).
 *
 * Angular excludes disabled controls from `form.valid`, so a required-but-
 * disabled control that the pure validator still checked would publish a
 * blocking X on the listing that the editor never renders — and that the user
 * could not clear, because a disabled field is uneditable by definition.
 *
 * On this sheet that is `ssmt:DescriptionOfBusiness` (spec 02 fields #10/#12/
 * #14 — `disabled: true`, `auto_managed: from_field:[#f9|#f11|#f13]`). Its
 * value is derived from the paired MSIC code by the editor's
 * `populateDescriptionsFromMsicCodes()`, so the mandatory error belongs to the
 * MSIC Code driver ([#f9], `required: true` and enabled), exactly as
 * `ssmt-dei:TypeOfSubmission` defers to its Basis driver on role0/role1.
 *
 * Role 2 declares no `ui.disabledWhen` rules, so — unlike the filing-info
 * helper — this needs no sheet data and stays a pure schema projection. Add
 * the `disabledWhen` branch here (and make the caller data-dependent) if one
 * is ever introduced.
 *
 * NOTE: this replaced a `ROLE2_REQUIRED_KEY_ALIASES` map whose sole entry let
 * `DescriptionOfBusiness[BusinessOneMember]` be satisfied by its sibling MSIC
 * code, so a freshly-converted sheet (backend ships the code, the editor
 * derives the description) would not paint a false X. Excluding the key
 * outright covers that case and the empty-MSIC case together, so the alias
 * machinery was removed rather than left holding an empty map.
 */
export function isRole2ControlDisabled(
  control: Role2RequiredDetectable
): boolean {
  return control.ui?.readonly === true;
}

/**
 * True when the control's validators array contains a `required` entry that
 * has NO `conditionalOn` property — i.e. an unconditional required declared
 * via the validators array rather than the top-level flag. Mirrors the role 6
 * fix so the two lifts stay consistent.
 */
export function hasUnconditionalRequiredViaValidators(
  control: Role2RequiredDetectable
): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn === undefined
    ) ?? false
  );
}

/**
 * True when buildForm() should attach `Validators.required` to the control.
 * Honours BOTH `control.required === true` AND an unconditional `required`
 * entry in the validators array. No field in role-2.form.json combines these
 * with a conditionalOn-flavoured required, so the role 6
 * "hasConditionalRequirement" guard is not needed here — if/when such a
 * field is added, extend this helper and both callers will pick it up.
 */
export function isRole2UnconditionallyRequired(
  control: Role2RequiredDetectable
): boolean {
  return (
    (control.required ?? false) || hasUnconditionalRequiredViaValidators(control)
  );
}
